/**
 * PATH: packages/email-hub/src/feature/components/InboxListToolbar.tsx
 * FILE_ID: EMAILHUB-FEATURE-INBOX-LIST-TOOLBAR-001
 * PURPOSE: Render the stable first row of the two-row Inbox command surface.
 * WHY: Scope and primary actions stay visible; refinement no longer creates rows.
 */

import { Plus, Sparkles } from 'lucide-react'
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { cn } from '../../_internal/lib/utils'
import { newDraftId } from '../compose/composeDockStore'
import {
  INBOX_COMMAND_ROOMY_MIN_PX,
  INBOX_COMMAND_STANDARD_MIN_PX,
  resolveInboxCommandDensity,
  type InboxCommandDensity,
} from './inbox-command/contracts'
import InboxScopeBar, { type InboxScope } from './InboxScopeBar'
import { emailPath } from '../lib/basePath'
import { composePreviewLocationState } from '../pages/composePreviewState'

export type ToolbarTier = InboxCommandDensity
export const TOOLBAR_WIDE_PX = INBOX_COMMAND_ROOMY_MIN_PX
export const TOOLBAR_NARROW_PX = INBOX_COMMAND_STANDARD_MIN_PX
export const TOOLBAR_COMPACT_PX = TOOLBAR_NARROW_PX

function useToolbarTier() {
  const rootRef = useRef<HTMLDivElement>(null)
  const [tier, setTier] = useState<ToolbarTier>('standard')

  useLayoutEffect(() => {
    const element = rootRef.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const apply = (width: number) => {
      if (width > 0) setTier(resolveInboxCommandDensity(width))
    }
    apply(element.getBoundingClientRect().width)
    const observer = new ResizeObserver((entries) => {
      apply(entries[0]?.borderBoxSize?.[0]?.inlineSize
        ?? entries[0]?.contentRect.width
        ?? element.getBoundingClientRect().width)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return { rootRef, tier }
}

export interface InboxListToolbarProps {
  isInboxFolder: boolean
  folderTitle: string
  showScopeBar: boolean
  activeScope: InboxScope
  /** Optional folder totals for scope-tab badges (B-03). */
  scopeCounts?: Partial<Record<InboxScope, number>>
  rightTab: 'inbox' | 'ai'
  onRightTab: (tab: 'inbox' | 'ai') => void
  /** Open email, if the inbox already has one selected. */
  selectedEmailId?: string | null
  /**
   * Folder-level primary action rendered left of Compose (Binned passes
   * "Restore all"). Inbox leaves it unset; the AI toggle owns that slot.
   */
  folderAction?: ReactNode
}

export function InboxListToolbar({
  isInboxFolder,
  folderTitle,
  showScopeBar,
  activeScope,
  scopeCounts,
  rightTab,
  onRightTab,
  selectedEmailId,
  folderAction,
}: InboxListToolbarProps) {
  const navigate = useNavigate()
  const { rootRef, tier } = useToolbarTier()
  const compact = tier === 'compact'

  return (
    <div
      ref={rootRef}
      className="flex min-w-0 items-center gap-1.5"
      data-testid="inbox-list-toolbar"
      data-tier={tier}
      data-compact={compact ? 'true' : 'false'}
    >
      <div className="min-w-0 flex-1">
        {showScopeBar ? (
          <InboxScopeBar active={activeScope} counts={scopeCounts} />
        ) : (
          <h1
            className="truncate px-1 text-sm font-semibold text-foreground"
            data-testid="email-folder-title"
          >
            {folderTitle}
          </h1>
        )}
      </div>

      {isInboxFolder && (
        <button
          type="button"
          aria-pressed={rightTab === 'ai'}
          onClick={() => onRightTab(rightTab === 'ai' ? 'inbox' : 'ai')}
          title={
            rightTab === 'ai'
              ? 'Return to the email list'
              : 'Open AI Analysis for the selected email'
          }
          className={cn(
            'inline-flex h-8 shrink-0 items-center gap-1 rounded-md border px-2 text-xs transition-colors',
            rightTab === 'ai'
              ? 'border-violet-500/40 bg-violet-500/10 text-violet-700 dark:text-violet-300'
              : 'border-border text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
          data-testid="inbox-ai-toggle"
        >
          <Sparkles className="h-3.5 w-3.5" />
          {tier === 'roomy' && <span>AI</span>}
        </button>
      )}

      {folderAction}

      <button
        type="button"
        onClick={() =>
          navigate(
            `${emailPath('/compose')}?draft=${encodeURIComponent(newDraftId())}`,
            composePreviewLocationState(selectedEmailId)
              ? { state: composePreviewLocationState(selectedEmailId) }
              : undefined,
          )
        }
        title="Compose a new email"
        aria-label="Compose a new email"
        data-testid="email-compose-button"
        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-primary px-2.5 text-xs font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
      >
        <Plus className="h-3.5 w-3.5" />
        {!compact && <span>Compose</span>}
      </button>
    </div>
  )
}

export default InboxListToolbar
